import { useEffect, useRef } from "react";

interface Streak {
    startX: number;
    startY: number;
    endX: number;
    endY: number;
    ctrlX: number;
    ctrlY: number;
    progress: number;
    speed: number;
    symbol: string;
}

interface Ripple {
    x: number;
    y: number;
    radius: number;
    maxRadius: number;
    alpha: number;
    symbol: string;
    symbolAlpha: number;
}

const STREAK_COLOR = "0, 201, 98"; // brand green #00C962
const MAX_STREAKS = 7;
const SPAWN_INTERVAL_MS = 480;
const MIN_DISTANCE = 220;
const RIPPLE_MAX_RADIUS = 80;

// Trail rendered as a chain of small segments with fading alpha
const TRAIL_SEGMENTS = 14;
const TRAIL_FRACTION = 0.22;

// Weighted symbol pool — major currencies appear more often than the others
const CURRENCY_POOL: { symbol: string; weight: number }[] = [
    { symbol: "$", weight: 5 },   // USD
    { symbol: "€", weight: 5 },   // EUR
    { symbol: "£", weight: 5 },   // GBP
    { symbol: "₦", weight: 5 },   // NGN
    { symbol: "¥", weight: 1 },   // JPY / CNY
    { symbol: "₣", weight: 1 },   // CFA / Franc
    { symbol: "₹", weight: 1 },   // INR
    { symbol: "R", weight: 1 },   // ZAR
    { symbol: "₵", weight: 1 },   // GHS
    { symbol: "د.إ", weight: 1 }, // AED
];

const CURRENCY_TOTAL_WEIGHT = CURRENCY_POOL.reduce(
    (sum, c) => sum + c.weight,
    0
);

const pickWeightedSymbol = (): string => {
    let r = Math.random() * CURRENCY_TOTAL_WEIGHT;
    for (const c of CURRENCY_POOL) {
        r -= c.weight;
        if (r <= 0) return c.symbol;
    }
    return CURRENCY_POOL[0].symbol;
};

const easeInOut = (t: number): number =>
    t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

// Quadratic bezier helpers
const bezier = (t: number, p0: number, p1: number, p2: number): number =>
    (1 - t) * (1 - t) * p0 + 2 * (1 - t) * t * p1 + t * t * p2;

// Animated success-check badge — green circle with a check that draws itself in.
// drawProgress: 0 → no check drawn, 1 → fully drawn.
const drawCheckBadge = (
    c: CanvasRenderingContext2D,
    x: number,
    y: number,
    r: number,
    alpha: number,
    drawProgress: number
) => {
    if (r <= 0 || alpha <= 0) return;

    // Soft outer glow
    const glow = c.createRadialGradient(x, y, r * 0.7, x, y, r * 1.7);
    glow.addColorStop(0, `rgba(${STREAK_COLOR}, ${alpha * 0.45})`);
    glow.addColorStop(1, `rgba(${STREAK_COLOR}, 0)`);
    c.fillStyle = glow;
    c.beginPath();
    c.arc(x, y, r * 1.7, 0, Math.PI * 2);
    c.fill();

    // Filled green badge with a soft top-left highlight for depth
    const fill = c.createRadialGradient(
        x - r * 0.35,
        y - r * 0.35,
        r * 0.1,
        x,
        y,
        r
    );
    fill.addColorStop(0, `rgba(34, 220, 120, ${alpha})`);
    fill.addColorStop(1, `rgba(0, 162, 78, ${alpha})`);
    c.fillStyle = fill;
    c.beginPath();
    c.arc(x, y, r, 0, Math.PI * 2);
    c.fill();

    // Hairline white border for crispness
    c.strokeStyle = `rgba(255, 255, 255, ${alpha * 0.35})`;
    c.lineWidth = 0.8;
    c.beginPath();
    c.arc(x, y, r - 0.4, 0, Math.PI * 2);
    c.stroke();

    // Checkmark — two-segment polyline drawn progressively
    const p1x = x - r * 0.42;
    const p1y = y + r * 0.02;
    const p2x = x - r * 0.08;
    const p2y = y + r * 0.32;
    const p3x = x + r * 0.46;
    const p3y = y - r * 0.3;

    const L1 = Math.hypot(p2x - p1x, p2y - p1y);
    const L2 = Math.hypot(p3x - p2x, p3y - p2y);
    const totalL = L1 + L2;
    const drawnL = totalL * Math.max(0, Math.min(1, drawProgress));

    if (drawnL > 0) {
        c.strokeStyle = `rgba(255, 255, 255, ${alpha})`;
        c.lineWidth = Math.max(1.3, r * 0.2);
        c.lineCap = "round";
        c.lineJoin = "round";
        c.beginPath();
        c.moveTo(p1x, p1y);
        if (drawnL <= L1) {
            const t = drawnL / L1;
            c.lineTo(p1x + (p2x - p1x) * t, p1y + (p2y - p1y) * t);
        } else {
            c.lineTo(p2x, p2y);
            const t2 = (drawnL - L1) / L2;
            c.lineTo(p2x + (p3x - p2x) * t2, p2y + (p3y - p2y) * t2);
        }
        c.stroke();
    }
};

const HeroBackgroundAnimation = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const reduceMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
        ).matches;
        if (reduceMotion) return;

        const streaks: Streak[] = [];
        const ripples: Ripple[] = [];
        let lastSpawn = 0;
        let last = performance.now();
        let rafId = 0;

        const resize = () => {
            const dpr = window.devicePixelRatio || 1;
            const rect = canvas.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) return;
            canvas.width = Math.floor(rect.width * dpr);
            canvas.height = Math.floor(rect.height * dpr);
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };
        resize();

        const ro = new ResizeObserver(resize);
        ro.observe(canvas);

        const spawnStreak = () => {
            const w = canvas.clientWidth;
            const h = canvas.clientHeight;
            const startX = Math.random() * w;
            const startY = Math.random() * h;
            let endX = Math.random() * w;
            let endY = Math.random() * h;

            let attempts = 0;
            while (
                Math.hypot(endX - startX, endY - startY) < MIN_DISTANCE &&
                attempts < 5
            ) {
                endX = Math.random() * w;
                endY = Math.random() * h;
                attempts += 1;
            }

            // Curve the path: control point offset perpendicular to the chord
            const midX = (startX + endX) / 2;
            const midY = (startY + endY) / 2;
            const dx = endX - startX;
            const dy = endY - startY;
            const len = Math.hypot(dx, dy) || 1;
            const perpX = -dy / len;
            const perpY = dx / len;
            const curveAmount = (Math.random() - 0.5) * len * 0.45;

            streaks.push({
                startX,
                startY,
                endX,
                endY,
                ctrlX: midX + perpX * curveAmount,
                ctrlY: midY + perpY * curveAmount,
                progress: 0,
                speed: 0.0042 + Math.random() * 0.004,
                symbol: pickWeightedSymbol(),
            });
        };

        const tick = (now: number) => {
            const dt = Math.min(now - last, 64);
            last = now;
            const step = dt / 16;

            if (
                now - lastSpawn > SPAWN_INTERVAL_MS &&
                streaks.length < MAX_STREAKS
            ) {
                spawnStreak();
                lastSpawn = now;
            }

            const w = canvas.clientWidth;
            const h = canvas.clientHeight;
            ctx.clearRect(0, 0, w, h);

            // ----- Streaks -----
            for (let i = streaks.length - 1; i >= 0; i--) {
                const s = streaks[i];
                s.progress += s.speed * step;

                if (s.progress >= 1) {
                    ripples.push({
                        x: s.endX,
                        y: s.endY,
                        radius: 0,
                        maxRadius: RIPPLE_MAX_RADIUS,
                        alpha: 0.6,
                        symbol: s.symbol,
                        symbolAlpha: 1,
                    });
                    streaks.splice(i, 1);
                    continue;
                }

                const tEased = easeInOut(s.progress);

                // Trail: chain of segments along the bezier behind the head, fading toward tail
                let prevX = 0;
                let prevY = 0;
                for (let j = 0; j <= TRAIL_SEGMENTS; j++) {
                    const localT = j / TRAIL_SEGMENTS;
                    const sampleT = Math.max(
                        0,
                        tEased - TRAIL_FRACTION * (1 - localT)
                    );
                    const px = bezier(sampleT, s.startX, s.ctrlX, s.endX);
                    const py = bezier(sampleT, s.startY, s.ctrlY, s.endY);

                    if (j > 0) {
                        const segAlpha = localT * localT * 0.85;
                        ctx.strokeStyle = `rgba(${STREAK_COLOR}, ${segAlpha})`;
                        ctx.lineWidth = 1.6 + localT * 0.9;
                        ctx.lineCap = "round";
                        ctx.beginPath();
                        ctx.moveTo(prevX, prevY);
                        ctx.lineTo(px, py);
                        ctx.stroke();
                    }
                    prevX = px;
                    prevY = py;
                }

                // Head position
                const hx = bezier(tEased, s.startX, s.ctrlX, s.endX);
                const hy = bezier(tEased, s.startY, s.ctrlY, s.endY);

                // Glow halo behind the symbol token
                const haloR = 16;
                const halo = ctx.createRadialGradient(
                    hx,
                    hy,
                    0,
                    hx,
                    hy,
                    haloR
                );
                halo.addColorStop(0, `rgba(${STREAK_COLOR}, 0.55)`);
                halo.addColorStop(0.6, `rgba(${STREAK_COLOR}, 0.18)`);
                halo.addColorStop(1, `rgba(${STREAK_COLOR}, 0)`);
                ctx.fillStyle = halo;
                ctx.beginPath();
                ctx.arc(hx, hy, haloR, 0, Math.PI * 2);
                ctx.fill();

                // Currency symbol rides at the head
                ctx.fillStyle = "rgba(255, 255, 255, 0.95)";
                ctx.font =
                    '600 13px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.fillText(s.symbol, hx, hy);
            }

            // ----- Ripples + lingering symbols -----
            for (let i = ripples.length - 1; i >= 0; i--) {
                const r = ripples[i];
                r.radius += 0.9 * step;
                r.alpha -= 0.009 * step;
                r.symbolAlpha -= 0.013 * step;

                if (r.alpha <= 0 && r.symbolAlpha <= 0) {
                    ripples.splice(i, 1);
                    continue;
                }

                if (r.alpha > 0 && r.radius < r.maxRadius) {
                    const glow = ctx.createRadialGradient(
                        r.x,
                        r.y,
                        0,
                        r.x,
                        r.y,
                        r.radius
                    );
                    glow.addColorStop(0, `rgba(${STREAK_COLOR}, 0)`);
                    glow.addColorStop(
                        0.55,
                        `rgba(${STREAK_COLOR}, ${r.alpha * 0.35})`
                    );
                    glow.addColorStop(
                        0.85,
                        `rgba(${STREAK_COLOR}, ${r.alpha * 0.18})`
                    );
                    glow.addColorStop(1, `rgba(${STREAK_COLOR}, 0)`);
                    ctx.fillStyle = glow;
                    ctx.beginPath();
                    ctx.arc(r.x, r.y, r.radius, 0, Math.PI * 2);
                    ctx.fill();
                }

                if (r.symbolAlpha > 0) {
                    // Currency symbol shifted up to sit above the badge
                    ctx.fillStyle = `rgba(255, 255, 255, ${r.symbolAlpha * 0.9})`;
                    ctx.font =
                        '600 13px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
                    ctx.textAlign = "center";
                    ctx.textBaseline = "middle";
                    ctx.fillText(r.symbol, r.x, r.y - 10);

                    // Check badge — pops in slightly, draws check stroke over first ~30% of life
                    const lifeT = 1 - r.symbolAlpha;
                    const popIn = Math.min(1, lifeT / 0.18);
                    const drawProgress = Math.min(1, lifeT / 0.3);
                    const badgeR = 8 * (0.7 + 0.3 * popIn);
                    drawCheckBadge(
                        ctx,
                        r.x,
                        r.y + 11,
                        badgeR,
                        r.symbolAlpha,
                        drawProgress
                    );
                }
            }

            rafId = requestAnimationFrame(tick);
        };

        rafId = requestAnimationFrame(tick);

        const handleVisibility = () => {
            if (document.hidden) {
                cancelAnimationFrame(rafId);
            } else {
                last = performance.now();
                rafId = requestAnimationFrame(tick);
            }
        };
        document.addEventListener("visibilitychange", handleVisibility);

        return () => {
            cancelAnimationFrame(rafId);
            ro.disconnect();
            document.removeEventListener("visibilitychange", handleVisibility);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full pointer-events-none"
            aria-hidden="true"
        />
    );
};

export default HeroBackgroundAnimation;
